/**
 * Brewmaster Service
 *
 * Frontend orchestrator for the Brewmaster chatbot and D&D-to-Daggerheart
 * conversion pipeline. Two modes:
 *
 * 1. Chat mode (chatMessage): Conversational SRD companion with function calling.
 *    The Brewmaster decides when to propose conversions, look up cards, or revise
 *    sections based on conversation context.
 *
 * 2. Legacy mode (proposeConversion, confirmConversion, etc.): Direct Cloud Function
 *    calls for the three-stage pipeline. Kept for backward compatibility.
 *
 * Part of the Brewmaster NPC feature (Issue #182).
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Send a chat message to the Brewmaster.
 * The Cloud Function handles conversation, SRD lookups, conversion proposals,
 * and section revisions via Gemini function calling.
 *
 * @param {string} userId
 * @param {Array<{role: string, content: string}>} messages - Conversation history
 * @param {object} [attachments] - Optional attachments (e.g., { pdf: base64string })
 * @returns {Promise<{ response: string, proposal?: object, characterId?: string, toolsUsed: string[] }>}
 */
export async function chatMessage(userId, messages, attachments) {
  const fns = getFunctions();
  const chat = httpsCallable(fns, 'brewmasterChat', { timeout: 180000 });
  const result = await chat({ messages, attachments });
  return result.data;
}

/**
 * Phase 1: Parse PDF and generate conversion proposal.
 * No Firestore writes — returns a proposal for player review.
 *
 * @param {string} userId
 * @param {string} pdfBase64 - PDF file as base64 string
 * @param {function} onProgress - Callback: ('parsing' | 'mapping') => void
 * @returns {Promise<{ parsedCharacter: object, proposal: object }>}
 */
export async function proposeConversion(userId, pdfBase64, onProgress) {
  const functions = getFunctions();

  // Stage 1: Parse PDF (can take ~50-60s for multimodal Gemini call)
  if (onProgress) onProgress('parsing');
  const parse = httpsCallable(functions, 'parseDnDCharacter', { timeout: 180000 });
  const parseResult = await parse({ pdfBase64, userId });
  const parsedCharacter = parseResult.data.parsedCharacter;

  // Stage 2: Map to Daggerheart (can take ~80s for SRD-heavy prompt)
  if (onProgress) onProgress('mapping');
  const map = httpsCallable(functions, 'mapToDaggerheart', { timeout: 180000 });
  const mapResult = await map({ parsedCharacter, userId });

  return {
    parsedCharacter,
    proposal: mapResult.data.proposal,
  };
}

/**
 * Phase 2: Player confirmed — build and save everything.
 *
 * @param {string} userId
 * @param {object} approvedPlan - The (potentially modified) conversion proposal
 * @returns {Promise<{ characterId: string, homebrewIds: string[], warnings: object[] }>}
 */
export async function confirmConversion(userId, approvedPlan) {
  const functions = getFunctions();
  const build = httpsCallable(functions, 'buildDaggerheartCharacter');
  const result = await build({ conversionPlan: approvedPlan, userId });
  return result.data;
}

/**
 * Re-run Stage 2 for a single section with player instructions.
 *
 * @param {string} userId
 * @param {object} parsedCharacter - Stage 1 output (preserved from first run)
 * @param {object} previousProposal - Current proposal
 * @param {string} section - Section to regenerate ('ancestry', 'class', 'community', 'domainCards', 'traits', 'equipment')
 * @param {string} instructions - Player's instructions (e.g. "use Faun instead of Halfling")
 * @returns {Promise<object>} Updated proposal
 */
export async function regenerateSection(userId, parsedCharacter, previousProposal, section, instructions) {
  const functions = getFunctions();
  const map = httpsCallable(functions, 'mapToDaggerheart', { timeout: 180000 });
  const result = await map({
    parsedCharacter,
    userId,
    regenerateSection: section,
    userInstructions: instructions,
    previousProposal,
  });
  return result.data.proposal;
}

/**
 * Create a proposal from a conversational description (no PDF).
 * Builds a structured summary matching Stage 1 output shape with nullable fields.
 *
 * @param {string} userId
 * @param {object} description - { name, level, class, species, concept, features }
 * @returns {Promise<{ proposal: object, parsedCharacter: object }>}
 */
export async function proposeFromDescription(userId, description) {
  const functions = getFunctions();

  const conversationalContext = {
    name: description.name || null,
    level: description.level || null,
    class: description.class || null,
    species: description.species || null,
    features: description.features || [],
    spells: [],
    weapons: [],
    equipment: [],
    backstory: description.concept || '',
    source: 'conversational',
  };

  const map = httpsCallable(functions, 'mapToDaggerheart', { timeout: 180000 });
  const result = await map({ conversationalContext, userId });
  return { proposal: result.data.proposal, parsedCharacter: conversationalContext };
}
